import {Stack, Transition} from "@mantine/core";
import classes from "./ChatSplashView.module.css";
import {useCallback, useEffect, useMemo, useState} from "react";
import {addMessage, clearThread} from "../chatSlice";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import {KAIResponseType, MessageWithMetadata} from "../../../types";
import {chatApi} from "../chatApi";
import {useNavigate, useSearchParams} from "react-router-dom";
import AskAboutAnimation from "./AskAboutAnimation";
import Suggestions from "./Suggestions";
import Footer from "./Footer";
import {CommandInput} from "./CommandInput";
import {SplashInput} from "./SplashInput";
import {RecentSearches} from "./RecentSearches";
import {RecentThreads} from "./RecentThreads";

const initialUserMessage = {
    role: "user",
    content: "",
    position: 0,
    sources: [],
    isRegenerated: false,
    timestamp: new Date().toISOString(),
    thoughts: [],
    activities: [],
    pluginActivities: [],
} as MessageWithMetadata;

const initialAssistantMessage = {
    role: "assistant",
    content: "",
    position: 1,
    sources: [],
    isRegenerated: false,
    timestamp: new Date().toISOString(),
    thoughts: [],
    activities: [],
    pluginActivities: [],
};

const systemName = "DIB";

export function ChatSplashView() {
    const [sendMessage, {data, isLoading}] =
        chatApi.endpoints.sendMessage.useMutation();
    const chosenResponseType = useAppSelector(
        (state) => state.session.chosenResponseType
    );

    // Trigger init to populate caches with a single network call
    const initData = chatApi.endpoints.getInit.useQuery({threadCount: 3});

    // These hooks will use the populated cache, but can refetch if needed
    const recentSearches = initData.data?.recentTopics || [];

    const threadsData = initData.data?.threads || [];

    const sortedThreads = useMemo(() => {
        if (!threadsData?.items) return [];
        return [...threadsData.items].sort(
            (a, b) =>
                new Date(b.lastUpdated).getTime() -
                new Date(a.lastUpdated).getTime()
        );
    }, [threadsData]);

    const [value, setValue] = useState("");

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const handleSendMessage = useCallback(
        (message: string) => {
            dispatch(clearThread());

            navigate(`/chat/new`);

            // Immediately add temporary messages to the thread
            dispatch(
                addMessage({
                    ...initialUserMessage,
                    content: message,
                    timestamp: new Date().toISOString(),
                } as MessageWithMetadata)
            );
            dispatch(
                addMessage({
                    ...initialAssistantMessage,
                    responseType: chosenResponseType,
                    timestamp: new Date().toISOString(),
                } as MessageWithMetadata)
            );

            sendMessage({
                system: {name: systemName},
                message,
                onThreadIdReceived: (threadId) => {
                    navigate(`/chat/${threadId}`, {
                        replace: true,
                        state: {from: "/"},
                    });
                },
            });
        },
        [navigate, sendMessage, chosenResponseType, dispatch]
    );

    // If the query parameter is set, send the message and clear the parameter
    const [searchParams, setSearchParams] = useSearchParams();
    const initialQuery = searchParams.get("q");
    useEffect(() => {
        if (initialQuery) {
            searchParams.delete("q");
            handleSendMessage(decodeURIComponent(initialQuery));
        }
    }, [initialQuery, handleSendMessage, setSearchParams, searchParams]);

    return (
        <>
            <Stack className={classes.chatContainer}>
                <Stack gap={0} className={classes.frontPageContainer}>
                    <Stack className={classes.headerSection}>
                        <Stack className={classes.headerContent}>
                            <AskAboutAnimation />
                            <SplashInput
                                value={value}
                                onSendMessage={handleSendMessage}
                                onValueChange={setValue}
                            />
                            <Transition
                                mounted={sortedThreads.length > 0}
                                transition="fade"
                                duration={300}
                            >
                                {(styles) => (
                                    <div style={styles}>
                                        <RecentThreads data={sortedThreads} />
                                    </div>
                                )}
                            </Transition>
                            <Transition
                                mounted={
                                    recentSearches && recentSearches.length > 0
                                }
                                transition="fade"
                                duration={300}
                            >
                                {(styles) => (
                                    <div style={styles}>
                                        <RecentSearches data={recentSearches} />
                                    </div>
                                )}
                            </Transition>
                        </Stack>
                    </Stack>
                    <Footer variant={"detailed"} />
                </Stack>
            </Stack>
        </>
    );
}
